import ADIPDPProductBrand, { ADIPDPProductBrandStyles } from "@NA/Components/ADIPDPProductBrand";
import ADIProductShareLink, { ADIProductShareLinkStyles } from "@NA/Components/ADIProductShareLink";
import ProductComapreCheckbox from "@NA/Widgets/ProductCompare/ProductCompareCheckbox";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ProductDetailsPageContext } from "@insite/content-library/Pages/ProductDetailsPage";
import { StyledButton } from "@insite/mobius/Clickable";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import * as React from "react";
import { css } from "styled-components";

type Props = WidgetProps & HasProduct;

export interface ProductDetailsPageTitleStyles {
    container?: GridContainerProps;
    brandGridItem?: GridItemProps;
    brandStyles?: ADIPDPProductBrandStyles;
    titleGridItem?: GridItemProps;
    productNumberGridItem?: GridItemProps;
    titleText?: TypographyProps;
    productNumberText?: TypographyProps;
    shareGridItem?: GridItemProps;
    link?: ADIProductShareLinkStyles;
    productCompareCheckbox?: InjectableCss;
}

export const pageTitleStyles: ProductDetailsPageTitleStyles = {
    container: {
        gap: 0,
        css: css`
            position: relative;
        `,
        offsetCss: css`
            max-width: 100%;
        `,
    },
    brandGridItem: {
        width: 12,
    },
    brandStyles: {
        logoImage: {
            css: css`
                img {
                    max-width: 150px;
                    max-height: 150px;
                }
            `,
        },
        nameText: {
            size: 16,
            weight: "normal",
        },
    },
    titleGridItem: {
        width: 12,
    },
    productNumberGridItem: {
        width: 12,
        css: css`
            display: flex;
            justify-content: space-between;
        `,
    },
    titleText: {
        variant: "h1",
        css: css`
            margin: 6px 0;
            font-size: 22px;
            line-height: 28px;
            color: ${getColor("text.main")};
            font-weight: 600;
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(
                theme,
                [
                    null,
                    null,
                    css`
                        font-size: 18px;
                        line-height: 24px;
                    `,
                ],
                "max",
            )}
        `,
    },
    productNumberText: {
        css: css`
        font-size: 14px;
        color: ${getColor("text.accent")};
        margin-bottom: 0px;
        > ${TypographyStyle} {
            position: relative;
            top: -1px;
            margin: 0 7px;
        }
        `
    },
    shareGridItem: {
        css: css`
            position: absolute;
            right: 0;
            top: 5px;
            >${StyledButton}{
                &:focus{
                    outline: none;
                }
            }
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(
                theme,
                [
                    null,
                    null,
                    css`
                        display: none;
                    `,
                ],
                "max",
            )}
        `,
    },
    productCompareCheckbox: {
        css: css`
            width: 100px;
        `,
    },
};

const styles = pageTitleStyles;

const ProductDetailsPageTitle: React.FC<Props> = ({ product }) => {
    return (
        <GridContainer {...styles.container}>
            {product.brand && (
                <GridItem {...styles.brandGridItem}>
                    <ADIPDPProductBrand brand={product.brand} showLogo={true} extendedStyles={styles.brandStyles} />
                </GridItem>
            )}
            <GridItem {...styles.shareGridItem}>
                <ADIProductShareLink extendedStyles={styles.link} />
            </GridItem>
            <GridItem {...styles.titleGridItem}>
                <Typography {...styles.titleText}>
                    {product.productTitle}
                </Typography>
            </GridItem>
            <GridItem {...styles.productNumberGridItem}>
                <Typography {...styles.productNumberText}>
                    {product.detail?.modelNumber}<Typography as="span">|</Typography>{product.productNumber}
                </Typography>
            </GridItem>
            
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(ProductDetailsPageTitle),
    definition: {
        displayName: "Page Title",
        group: "Product Details",
        allowedContexts: [ProductDetailsPageContext],
    },
};

export default widgetModule;
